/**
 * Builds the `/pick/submit` request body from the fan's selection, and
 * classifies a refusal into "show it inline and refetch" versus "raise the
 * shared error modal". Pure, so every error branch is tested without
 * rendering or mocking a network.
 */
import { extractErrorCode } from "../../errors/apiErrorBus";
import { PICK_ROUND_LOCKED_NOTE, PICK_STALE_BOARD_NOTE } from "./pickCopy";

export type typePickSelection = {
  eventId: string;
  teamId: string;
  acceptsPendingSpread: boolean;
};

export function buildPickSubmitPayload(input: {
  poolId: string;
  contestId: string;
  roundId: string;
  entryId: string;
  requestId: string;
  selections: readonly typePickSelection[];
}): {
  poolId: string;
  contestId: string;
  roundId: string;
  entryId: string;
  requestId: string;
  picks: { eventId: string; teamId: string; acceptsPendingSpread: boolean }[];
} {
  return {
    poolId: input.poolId,
    contestId: input.contestId,
    roundId: input.roundId,
    entryId: input.entryId,
    requestId: input.requestId,
    picks: input.selections.map((selection) => ({
      eventId: selection.eventId,
      teamId: selection.teamId,
      acceptsPendingSpread: selection.acceptsPendingSpread,
    })),
  };
}

export type typePickSubmitOutcome =
  | { kind: "ok" }
  | { kind: "inline"; code: string; note: string }
  | { kind: "modal"; code: string };

export function classifyPickSubmitError(
  result: unknown,
): typePickSubmitOutcome {
  const code = extractErrorCode(result);
  if (code === null) return { kind: "ok" };
  const reason =
    typeof result === "object" && result !== null
      ? (result as { error?: { details?: { reason?: unknown } } }).error
          ?.details?.reason
      : undefined;
  if (code === "CONFLICT" && reason === "pickRequirementChangedRefreshBoard") {
    return {
      kind: "inline",
      code,
      note: "Your required pick count changed. Review the refreshed round and submit the updated picks.",
    };
  }

  if (code === "ROUND_LOCKED") {
    return { kind: "inline", code, note: PICK_ROUND_LOCKED_NOTE };
  }
  if (code === "TEAM_NOT_SELECTABLE" || code === "NEEDS_SPREAD_CONSENT") {
    return { kind: "inline", code, note: PICK_STALE_BOARD_NOTE };
  }
  return { kind: "modal", code };
}
